import React from "react";
import { MuiOtpInput } from "mui-one-time-password-input";
import { z } from "zod";
import StackKit from "../../kit/stack";
import { toPersianNumber } from "@/hooks/useThousandsSeparator";
import TypoGraphyKit from "../../kit/typoGraphy";

type OtpInputProps = {
    length?: number;
    onComplete?: (otp: string) => void;
    onChange?: (otp: string) => void;
    autoFocus?: boolean;
    disabled?: boolean;
    value?: string;
    onResend?: any;
    onApiCall?: () => void;
    hasResend?: boolean;
    handleSubmit?: any;
    // New custom timer props
    initialTimer?: number;
    onTimerComplete?: () => void;
    externalTimeLeft?: number;
    setExternalTimeLeft?: (time: number) => void;
    disableTimer?: boolean; // Optional: to completely disable timer
};

const otpSchema = z.string().length(6).regex(/^\d+$/, {
    message: "OTP باید فقط عدد باشد و دقیقاً ۶ رقم داشته باشد",
});

const OtpInput = ({
                      length = 6,
                      onComplete,
                      onChange,
                      handleSubmit,
                      autoFocus = true,
                      disabled = false,
                      value: controlledValue,
                      onResend,
                      hasResend,
                      onApiCall = () => {},
                      initialTimer = 120,
                      onTimerComplete,
                      externalTimeLeft,
                      setExternalTimeLeft,
                      disableTimer = false,
                      ...rest
                  }: OtpInputProps) => {
    const [otp, setOtp] = React.useState(controlledValue || "");

    // Internal timer state
    const [internalTimeLeft, setInternalTimeLeft] = React.useState(initialTimer);
    const [resendVisible, setResendVisible] = React.useState(false);

    // Determine which timer to use (external or internal)
    const isExternalTimer = externalTimeLeft !== undefined && setExternalTimeLeft !== undefined;
    const timeLeft = isExternalTimer ? externalTimeLeft : internalTimeLeft;

    // Handler for decreasing time
    const decreaseTime = React.useCallback(() => {
        if (isExternalTimer && setExternalTimeLeft) {
            setExternalTimeLeft(timeLeft - 1);
        } else {
            setInternalTimeLeft((prev: number) => prev - 1);
        }
    }, [isExternalTimer, setExternalTimeLeft, timeLeft]);

    // Handler for resetting time
    const resetTime = React.useCallback(() => {
        if (isExternalTimer && setExternalTimeLeft) {
            setExternalTimeLeft(initialTimer);
        } else {
            setInternalTimeLeft(initialTimer);
        }
        setResendVisible(false);
    }, [isExternalTimer, setExternalTimeLeft, initialTimer]);

    // Timer effect
    React.useEffect(() => {
        // Don't run timer if disabled or if time is already 0
        if (disableTimer || timeLeft <= 0) {
            if (timeLeft <= 0 && !resendVisible) {
                setResendVisible(true);
                if (onTimerComplete) {
                    onTimerComplete();
                }
            }
            return;
        }

        const timer = setInterval(() => {
            decreaseTime();
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft, decreaseTime, disableTimer, onTimerComplete, resendVisible]);

    const formatTime = (seconds: number) => {
        const min = Math.floor(seconds / 60)
            .toString()
            .padStart(2, "0");
        const sec = (seconds % 60).toString().padStart(2, "0");
        return `${min}:${sec}`;
    };

    const handleChange = (newValue: string) => {
        setOtp(newValue);
        if (onChange) onChange(newValue);

        if (newValue.length === length) {
            const result = otpSchema.safeParse(newValue);
            if (!result.success) {
                console.error("Validation Error:", result.error.message);
                return;
            }

            if (onComplete) onComplete(newValue);
            if (onApiCall) onApiCall();
        }
    };

    const handleResend = () => {
        if (onResend) onResend();
        setOtp("");
        resetTime();
    };

    return (
        <StackKit direction="column" alignItems="center" sx={{ width: "100%", px: { xs: 2, sm: 3, md: 4 }, py: 5, mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, gap: 2 }}>
            <MuiOtpInput
                TextFieldsProps={{
                    inputProps: { dir: "ltr" },
                    InputProps: {
                        sx: {
                            backgroundColor: "#000",
                            width: { xs: 40, sm: 48 },
                            height: { xs: 40, sm: 48 },
                            border: "transparent",
                        },
                    },
                }}
                length={length}
                value={controlledValue ?? otp}
                onChange={handleChange}
                autoFocus={autoFocus}
                {...rest}
                sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    // 👇 mobile: small gap + centered, desktop: spread
                    gap: { xs: 0.5, sm: 2, md: 3 },
                    justifyContent: { xs: "center", sm: "space-between" },
                    width: { xs: "auto", sm: "100%" },
                    maxWidth:"400px",
                    flexWrap: "nowrap",
                    px: { xs: 0, sm: 0 },
                }}
            />

            {hasResend && !disableTimer && (
                <StackKit direction="column" alignItems="center" sx={{ mt: { xs: 2, sm: 3 }, width: "100%", fontSize: { xs: 12, sm: 14, md: 16 }, mb: { xs: 2, sm: 3, md: 4 } }}>
                    {resendVisible ? (
                        <StackKit direction="column" alignItems="center" sx={{ gap: { xs: 1, sm: 2 } }}>
                            <button
                                onClick={handleResend}
                                style={{
                                    color: "#136A74",
                                    fontSize: "1rem",
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "8px 16px",
                                    borderRadius: "4px",
                                    transition: "all 0.2s"
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(54, 135, 255, 0.1)"}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                            >
                                ارسال مجدد کد
                            </button>
                        </StackKit>
                    ) : (
                        <TypoGraphyKit sx={{ color: "#136A74", textAlign: "center" }}>
                            زمان باقی‌مانده: {toPersianNumber(formatTime(timeLeft))}
                        </TypoGraphyKit>
                    )}
                </StackKit>
            )}
        </StackKit>
    );
};

export default OtpInput;